// Shared input-mode rules for the AI logger. All three modes are valid: photo only, description
// only, or both. Single source of truth so the Start screen's validation and the Analyzing screen's
// route selection + error copy stay consistent (and unit-testable).

const hasText = (t) => Boolean(t && String(t).trim());
const hasPhoto = (p) => Boolean(p && String(p).length);

// A submission is valid when a photo OR a non-empty description is present.
export const canAnalyze = (photo, note) => hasPhoto(photo) || hasText(note);

// The Analyze button is disabled when there is NO usable input, or a photo is still being processed.
export const analyzeDisabled = (photo, note, picking) => (!canAnalyze(photo, note)) || Boolean(picking);

// Route selection. When the user is eligible for rebuild-v2 (server-reported), a photo -> the rebuild-v2
// image endpoint (description travels with it), description-only -> the rebuild-v2 text endpoint. When
// NOT eligible (flag OFF / not allowlisted), the existing legacy routes are used UNCHANGED. Returns null
// when neither input is present (caller must NOT proceed). `rebuildV2` defaults false (legacy).
export const pickDraftRoute = (imageB64, notes, rebuildV2 = false) => {
  const text = (notes || '').trim();
  if (rebuildV2) {
    if (hasPhoto(imageB64)) return { path: '/ai-log/draft/rebuild-v2/image', body: { image_base64: imageB64, user_text: text }, rebuild_v2: true };
    if (hasText(text)) return { path: '/ai-log/draft/rebuild-v2/text', body: { user_text: text }, rebuild_v2: true };
    return null;
  }
  if (hasPhoto(imageB64)) return { path: '/ai-log/draft/image', body: { image_base64: imageB64, user_text: text } };
  if (hasText(text)) return { path: '/ai-log/draft/text', body: { user_text: text } };
  return null;
};

// Map a backend error (HTTP status + detail) to a user-facing message. Neutral wording that works for
// photo, description, or both; surfaces the backend's own detail (incl. the specific 422
// insufficient-info message) whenever available.
export const analyzeErrorMessage = (status, detail) => {
  const detailMsg = typeof detail === 'string' ? detail : (detail && detail.message);
  if (status === 403) return detailMsg || 'AI logging is not enabled for this account yet.';
  if (status === 401) return 'Please sign in to use AI logging.';
  if (status === 422) return detailMsg || 'We couldn\u2019t identify enough food information. Add a clearer photo or describe the foods and portion sizes.';
  return detailMsg || 'Could not analyze this meal. Please check the photo or description and try again.';
};
